// src/components/MyAppointments.jsx — Appointments Page
// Layout mirrors "Check Points and Eligible Promotions" (LoyaltyProgram.jsx)
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getSalonConfig } from '../api/salonConfigService';
import { useSalonLocations } from '../hooks/useSalonLocations';
import '../styles/loyalty.css';
import '../styles/appointments-page.css';

// ─── helpers ────────────────────────────────────────────────────────────────

const getFontSizeValue = (fontSize) => {
  switch (fontSize) {
    case 'small':  return '14px';
    case 'large':  return '18px';
    default:       return '16px';
  }
};

const formatDateTime = (dt) => {
  if (!dt) return '';
  return dt.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

const formatDate = (dt) => {
  if (!dt) return '';
  return dt.toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
  });
};

const formatTime = (dt) => {
  if (!dt) return '';
  return dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const STATUS_META = {
  pending:   { label: 'Pending',   cls: 'appt-status-pending'   },
  approved:  { label: 'Confirmed', cls: 'appt-status-confirmed' },
  confirmed: { label: 'Confirmed', cls: 'appt-status-confirmed' },
  completed: { label: 'Completed', cls: 'appt-status-completed' },
  cancelled: { label: 'Cancelled', cls: 'appt-status-cancelled' },
  rejected:  { label: 'Rejected',  cls: 'appt-status-rejected'  },
};

const toDate = (v) => {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  return new Date(v);
};

// ─── component ──────────────────────────────────────────────────────────────

function MyAppointments() {
  const { salonId }     = useParams();
  const navigate        = useNavigate();
  const location        = useLocation();
  const { currentUser } = useCustomerAuth();

  const { getLocation } = useSalonLocations(salonId);

  const [allAppointments,     setAllAppointments]     = useState([]);
  const [loading,             setLoading]             = useState(true);
  const [error,               setError]               = useState('');
  const [successMessage,      setSuccessMessage]       = useState('');
  const [cancellingId,        setCancellingId]         = useState(null);

  // Section refs for navigation from sub-tabs (future use)
  const upcomingRef = useRef(null);
  const historyRef  = useRef(null);

  // ── Apply salon typography ───────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const cfg = await getSalonConfig(salonId);
        if (cfg?.branding) {
          const root = document.documentElement;
          const c = cfg.branding.colors || {};
          const t = cfg.branding.typography || {};
          root.style.setProperty('--salon-primary',      c.primary      || '#E64C31');
          root.style.setProperty('--salon-secondary',    c.secondary    || '#FFBDB1');
          root.style.setProperty('--salon-accent',       c.accent       || '#F6F6F6');
          root.style.setProperty('--salon-background',   c.background   || '#FFFFFF');
          root.style.setProperty('--salon-text',         c.text         || '#333333');
          root.style.setProperty('--salon-heading-font', t.headingFont  || 'Familjen Grotesk, sans-serif');
          root.style.setProperty('--salon-body-font',    t.bodyFont     || 'Wix Madefor Text, sans-serif');
          root.style.setProperty('--salon-font-size',    getFontSizeValue(t.fontSize));
        }
      } catch (e) {
        console.error('MyAppointments: typography load error', e);
      }
    };
    if (salonId) load();
  }, [salonId]);

  // ── Real-time appointments listener ─────────────────────────────────────
  useEffect(() => {
    if (!currentUser?.email || !salonId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsub = onSnapshot(
      query(
        collection(db, 'appointments'),
        where('customerEmail', '==', currentUser.email),
        where('salonId',       '==', salonId)
      ),
      (snap) => {
        const list = [];
        snap.forEach(d => {
          const a = d.data();
          const dateTime = toDate(a.dateTimeRequested);
          const created  = toDate(a.dateTimeCreated);
          list.push({ id: d.id, ...a, dateTimeRequested: dateTime, dateTimeCreated: created });
        });
        list.sort((a, b) => (b.dateTimeRequested || 0) - (a.dateTimeRequested || 0));
        setAllAppointments(list);
        setLoading(false);
      },
      (err) => {
        console.error('MyAppointments: listener error', err);
        setError('Failed to load appointments.');
        setLoading(false);
      }
    );

    return () => unsub();
  }, [currentUser?.email, salonId]);

  // ── Scroll to section on arrival from sub-tab nav ───────────────────────
  useEffect(() => {
    const section = location.state?.section;
    if (!section || loading) return;
    const refMap = { 'upcoming': upcomingRef, 'history': historyRef };
    const target = refMap[section];
    if (target?.current) {
      setTimeout(() => target.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [location.state?.section, loading]);

  // ── Messages ─────────────────────────────────────────────────────────────
  const showMsg = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccessMessage(''); }
    else         { setSuccessMessage(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccessMessage(''); }, 5000);
  };

  // ── Cancel ───────────────────────────────────────────────────────────────
  const isWithin24Hours = (dt) => {
    if (!dt) return true;
    return (dt.getTime() - Date.now()) < 24 * 60 * 60 * 1000;
  };

  const handleCancel = async (appt) => {
    if (isWithin24Hours(appt.dateTimeRequested)) {
      showMsg('Cancellation must be made at least 24 hours before the appointment.', true);
      return;
    }
    try {
      setCancellingId(appt.id);
      await updateDoc(doc(db, 'appointments', appt.id), {
        status: 'cancelled', cancelledAt: new Date(), updatedAt: new Date(),
        cancelledBy: 'customer', // drives the admin alert bell
      });
      showMsg('Appointment cancelled successfully.');
    } catch (e) {
      console.error('Cancel error:', e);
      showMsg('Failed to cancel. Please try again.', true);
    } finally {
      setCancellingId(null);
    }
  };

  // ── Reschedule ───────────────────────────────────────────────────────────
  const handleReschedule = (appt) => {
    if (isWithin24Hours(appt.dateTimeRequested)) {
      showMsg('Rescheduling must be made at least 24 hours before the appointment.', true);
      return;
    }
    navigate(`/BespokeBeauty/${salonId}/customer/book`, {
      state: {
        isReschedule: true,
        originalAppointment: appt,
        rescheduleData: {
          originalAppointmentId: appt.id,
          appointmentId:         appt.appointmentId,
          staffId:               appt.staffId,
          staffName:             appt.staffName,
          serviceId:             appt.serviceRequested?.id,
          serviceName:           appt.serviceRequested?.name,
          serviceDuration:       appt.serviceRequested?.duration,
          servicePrice:          appt.serviceRequested?.price,
          originalDateTime:      appt.dateTimeRequested,
          notes:                 appt.notes || '',
        },
      },
    });
  };

  // ── Split upcoming vs history ─────────────────────────────────────────────
  const now = new Date();
  const upcoming = allAppointments.filter(a =>
    ['pending', 'approved', 'confirmed'].includes(a.status) &&
    a.dateTimeRequested && a.dateTimeRequested > now
  );
  const history = allAppointments.filter(a =>
    !(['pending', 'approved', 'confirmed'].includes(a.status) && a.dateTimeRequested > now)
  );

  // ── Skeleton loader ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="ap-loyalty-container">
        <div className="skel-hero-bar" />
        <div className="skel-section">
          {[1, 2].map(i => (
            <div key={i} className="skel-appt-card" style={{ marginBottom: 12 }}>
              <div className="skel-avatar" />
              <div className="skel-lines">
                <div className="skel-line skel-line--lg" />
                <div className="skel-line skel-line--md" />
                <div className="skel-line skel-line--sm" />
              </div>
            </div>
          ))}
        </div>
        <div className="skel-section">
          {[1, 2, 3].map(i => (
            <div key={i} className="skel-appt-card" style={{ marginBottom: 12 }}>
              <div className="skel-avatar" />
              <div className="skel-lines">
                <div className="skel-line skel-line--lg" />
                <div className="skel-line skel-line--md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Appointment card ─────────────────────────────────────────────────────
  const renderApptCard = (appt, showActions = false) => {
    const meta   = STATUS_META[appt.status?.toLowerCase()] || { label: appt.status, cls: '' };
    const locked = isWithin24Hours(appt.dateTimeRequested);
    return (
      <div key={appt.id} className="appt-card">
        <div className="appt-card-header">
          <div className="appt-card-service">
            <span className="appt-service-name">
              {appt.serviceRequested?.name || 'Service'}
            </span>
            {appt.serviceRequested?.duration && (
              <span className="appt-service-duration">
                {appt.serviceRequested.duration} min
              </span>
            )}
          </div>
          <span className={`appt-status-badge ${meta.cls}`}>{meta.label}</span>
        </div>

        <div className="appt-card-body">
          {appt.locationId && getLocation(appt.locationId) && (
            <div className="appt-detail-row">
              <svg className="appt-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M8 1.5C5.51 1.5 3.5 3.51 3.5 6c0 3.5 4.5 8.5 4.5 8.5s4.5-5 4.5-8.5c0-2.49-2.01-4.5-4.5-4.5z"/>
                <circle cx="8" cy="6" r="1.5"/>
              </svg>
              <span className="appt-location-name">{getLocation(appt.locationId).locationName}</span>
            </div>
          )}

          <div className="appt-detail-row">
            <svg className="appt-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <rect x="1" y="3" width="14" height="12" rx="2"/>
              <line x1="5" y1="1" x2="5" y2="5"/>
              <line x1="11" y1="1" x2="11" y2="5"/>
              <line x1="1" y1="7" x2="15" y2="7"/>
            </svg>
            <span>{appt.dateTimeRequested ? `${formatDate(appt.dateTimeRequested)} at ${formatTime(appt.dateTimeRequested)}` : 'Date not set'}</span>
          </div>

          <div className="appt-detail-row">
            <svg className="appt-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="8" cy="5" r="3"/>
              <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
            </svg>
            <span>with {appt.staffName || appt.stylistName || 'Staff'}</span>
          </div>

          {appt.notes && (
            <div className="appt-detail-row">
              <svg className="appt-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <rect x="2" y="2" width="12" height="12" rx="2"/>
                <line x1="5" y1="6" x2="11" y2="6"/>
                <line x1="5" y1="9" x2="9" y2="9"/>
              </svg>
              <span className="appt-notes">{appt.notes}</span>
            </div>
          )}

          {appt.cancelledByAdmin && appt.cancellationReason && (
            <div className="appt-cancellation-notice">
              <span className="appt-cancellation-label">Cancelled by salon:</span>
              <span className="appt-cancellation-reason">{appt.cancellationReason}</span>
            </div>
          )}

          {showActions && locked && (
            <p className="appt-locked-note">Changes not allowed within 24 hours of appointment.</p>
          )}
        </div>

        {showActions && (appt.status === 'pending' || appt.status === 'approved' || appt.status === 'confirmed') && (
          <div className="appt-card-actions">
            <button
              className="appt-btn appt-btn-reschedule"
              onClick={() => handleReschedule(appt)}
              disabled={locked}
              title={locked ? 'Cannot reschedule within 24 hours' : 'Reschedule'}
            >
              Reschedule
            </button>
            <button
              className="appt-btn appt-btn-cancel"
              onClick={() => handleCancel(appt)}
              disabled={cancellingId === appt.id || locked}
              title={locked ? 'Cannot cancel within 24 hours' : 'Cancel'}
            >
              {cancellingId === appt.id ? 'Cancelling…' : 'Cancel'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="ap-loyalty-container">
      <main className="ap-loyalty-main">

        {/* Page header */}
        <div className="ap-page-header">
          <h1 className="ap-page-title">Appointments</h1>
          <p style={{ margin: '6px 0 0', color: '#666', fontSize: '15px' }}>Manage your upcoming appointments and review your appointment history.</p>
        </div>

        {/* Messages */}
        {(error || successMessage) && (
          <div className="message-container">
            {error && (
              <div className="error-message">
                {error}
                <button onClick={() => setError('')}>Close</button>
              </div>
            )}
            {successMessage && (
              <div className="success-message">
                {successMessage}
                <button onClick={() => setSuccessMessage('')}>Close</button>
              </div>
            )}
          </div>
        )}

        {/* ── Upcoming Appointments ──────────────────────────────────────── */}
        <div
          className="ap-section"
          ref={upcomingRef}
          id="section-upcoming"
        >
          <div className="appt-section-heading">
            <h2>Upcoming Appointments</h2>
            <button
              className="appt-book-btn"
              onClick={() => navigate(`/BespokeBeauty/${salonId}/customer/book`)}
            >
              + Book New
            </button>
          </div>

          {upcoming.length === 0 ? (
            <div className="empty-state-illustrated" style={{ padding: '30px 0' }}>
              <svg className="empty-state-svg" viewBox="0 0 120 120" fill="none">
                <rect x="20" y="30" width="80" height="70" rx="8" fill="var(--salon-accent,#F6F6F6)" opacity="0.8"/>
                <rect x="20" y="30" width="80" height="16" rx="8" fill="var(--salon-primary,#E64C31)" opacity="0.25"/>
                <rect x="35" y="58" width="50" height="6" rx="3" fill="var(--salon-primary,#E64C31)" opacity="0.2"/>
                <rect x="42" y="70" width="36" height="6" rx="3" fill="var(--salon-primary,#E64C31)" opacity="0.15"/>
                <circle cx="60" cy="22" r="8" fill="var(--salon-primary,#E64C31)" opacity="0.4"/>
              </svg>
              <p className="empty-state-msg">No upcoming appointments</p>
              <p className="empty-state-sub">Ready for a fresh look?</p>
              <button
                className="empty-state-cta"
                onClick={() => navigate(`/BespokeBeauty/${salonId}/customer/book`)}
              >
                Book Now
              </button>
            </div>
          ) : (
            <div className="appt-list">
              {upcoming.map(a => renderApptCard(a, true))}
            </div>
          )}
        </div>

        {/* ── Appointment History ───────────────────────────────────────── */}
        <div
          className="ap-section"
          ref={historyRef}
          id="section-history"
        >
          <h2>Appointment History</h2>

          {history.length === 0 ? (
            <p className="appt-empty-note">No appointment history yet.</p>
          ) : (
            <div className="appt-list">
              {history.map(a => renderApptCard(a, false))}
            </div>
          )}
        </div>

      </main>
    </div>
  );
}

export default MyAppointments;
